import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { financialData, yearlyTotals, SEZZLE_NET, APP_PRIX_NET, previsionsY1Minimum, previsionsY1Realiste, objectifsY1, preSeedInvestment } from '../mock';
import { formatCurrency, formatNumber, formatCompact, formatCompactMobile } from '../lib/utils';
import { useIsMobile } from '../hooks/useIsMobile';
import { TrendingUp, DollarSign, Calculator, BarChart3, Copy, Check } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useScenario } from '../contexts/ScenarioContext';
import ScenarioToggle from '../components/ScenarioToggle';
import { translations } from '../translations';
import NavigationControls from '../components/NavigationControls';
import FinancialCharts from '../components/FinancialCharts';
import TimeSlider from '../components/TimeSlider';
import YearComparison from '../components/YearComparison';
import PartieBadge from '../components/PartieBadge';
import AcronymTooltip from '../components/AcronymTooltip';
import AnimatedCounter from '../components/AnimatedCounter';
import ProgressBar from '../components/ProgressBar';
import ExpandableTable from '../components/ExpandableTable';
import VideoPlayer from '../components/VideoPlayer';

export default function Previsions() {
  const { language } = useLanguage();
  const { scenario } = useScenario();
  const isMobile = useIsMobile();
  const fmt = (v) => isMobile ? formatCompactMobile(v) : formatCompact(v);
  const t = translations[language].forecast;
  const [selectedYear, setSelectedYear] = useState(Object.keys(financialData)[0]);
  const [calcValues, setCalcValues] = useState({
    repasJour: 1000,
    prixRepas: 7.50
  });
  const [copied, setCopied] = useState(false);

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const calculateProjection = () => {
    const repasJour = parseFloat(calcValues.repasJour) || 0;
    const prixRepas = parseFloat(calcValues.prixRepas) || 0;
    const parentsReguliers = Math.round(repasJour / 1.8);
    const txReguliers = Math.round(1.75 * parentsReguliers);
    const gmvRegulier = repasJour * 20 * prixRepas;
    const gmvSezzle = gmvRegulier * 0.15;
    const revenus = (0.025 * 0.85 * gmvRegulier) + (SEZZLE_NET * gmvSezzle) + (0.15 * txReguliers) + (APP_PRIX_NET * parentsReguliers * 0.5);
    const opex = revenus * 0.20;
    const baiia = revenus - opex;

    return {
      parentsReguliers,
      txReguliers,
      gmvRegulier,
      gmvSezzle,
      revenus,
      opex,
      baiia
    };
  };

  const projection = calculateProjection();

  return (
    <div className="min-h-screen p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        <PartieBadge path="/previsions" />
        <div className="space-y-8">
        {/* Header with copy link */}
        <div className="flex items-start justify-between mb-8">
          <div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              {language === 'fr' ? (
                <>Prévisions <span className="text-lime-400">Financières</span></>
              ) : (
                <>Financial <span className="text-lime-400">Forecast</span></>
              )}
            </h1>
            <p className="text-lg sm:text-xl text-gray-400">{t.subtitle}</p>
          </div>
          <Button
            onClick={copyLink}
            variant="outline"
            size="sm"
            className="border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10"
          >
            {copied ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
            {copied ? (language === 'fr' ? 'Copié !' : 'Copied!') : (language === 'fr' ? 'Copier lien' : 'Copy link')}
          </Button>
        </div>

        {/* === NOUVEAU PLAN PRÉ-SEED Y1 — 2 SCÉNARIOS === */}
        <Card className="bg-gradient-to-br from-lime-500/15 via-cyan-500/10 to-orange-500/10 border-2 border-lime-500/40 shadow-[0_0_30px_rgba(168,213,48,0.2)]" data-testid="previsions-y1-section">
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <span className="inline-block px-3 py-1 bg-lime-500 text-black text-[10px] font-bold rounded-full uppercase tracking-wider mb-2">
                  {language === 'fr' ? 'Pré-seed 500K — Année 1 mensuelle' : 'Pre-seed $500K — Year 1 monthly'}
                </span>
                <CardTitle className="text-2xl text-white">
                  {language === 'fr' ? 'Prévisions Mensuelles M1 → M12' : 'Monthly Forecast M1 → M12'}
                </CardTitle>
              </div>
              <ScenarioToggle />
            </div>
          </CardHeader>
          <CardContent>
            {(() => {
              const data = scenario === 'minimum' ? previsionsY1Minimum : previsionsY1Realiste;
              const obj = scenario === 'minimum' ? objectifsY1.minimum : objectifsY1.realiste;
              const m6 = data[5];
              const m12 = data[11];
              return (
                <div className="space-y-6">
                  {/* KPI Cards */}
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    <div className="p-3 bg-black/40 rounded-lg border border-lime-500/30">
                      <p className="text-[10px] text-gray-400 uppercase tracking-wider">{language === 'fr' ? 'M6 — Établissements' : 'M6 — Institutions'}</p>
                      <p className="text-2xl font-bold text-lime-400 mt-1">{m6.etablissements.toLocaleString()}</p>
                      <p className="text-[10px] text-gray-500">{m6.ecoles} {language === 'fr' ? 'écoles' : 'schools'} + {m6.garderies} {language === 'fr' ? 'garderies' : 'daycares'}</p>
                    </div>
                    <div className="p-3 bg-black/40 rounded-lg border border-cyan-500/30">
                      <p className="text-[10px] text-gray-400 uppercase tracking-wider">{language === 'fr' ? 'M12 — Établissements' : 'M12 — Institutions'}</p>
                      <p className="text-2xl font-bold text-cyan-400 mt-1">{m12.etablissements.toLocaleString()}</p>
                      <p className="text-[10px] text-gray-500">{m12.ecoles.toLocaleString()} + {m12.garderies.toLocaleString()}</p>
                    </div>
                    <div className="p-3 bg-black/40 rounded-lg border border-orange-500/30">
                      <p className="text-[10px] text-gray-400 uppercase tracking-wider">{language === 'fr' ? 'GMV annualisé M12' : 'M12 annualized GMV'}</p>
                      <p className="text-2xl font-bold text-orange-400 mt-1">{(m12.gmvAnnualise / 1000000).toFixed(0)}M $</p>
                      <p className="text-[10px] text-gray-500">{m12.repasJour.toLocaleString()} {language === 'fr' ? 'repas/jour' : 'meals/day'}</p>
                    </div>
                    <div className="p-3 bg-black/40 rounded-lg border border-purple-500/30">
                      <p className="text-[10px] text-gray-400 uppercase tracking-wider">{language === 'fr' ? 'Revenu annualisé M12' : 'M12 annualized revenue'}</p>
                      <p className="text-2xl font-bold text-purple-400 mt-1">{(obj.revenuAnnualiseM12 / 1000000).toFixed(2)}M $</p>
                      <p className="text-[10px] text-gray-500">{(m12.revenuMensuel / 1000).toFixed(0)}K $ / {language === 'fr' ? 'mois' : 'month'}</p>
                    </div>
                  </div>

                  {/* Table Opérations */}
                  <div className="overflow-x-auto">
                    <h4 className="text-sm font-semibold text-lime-400 mb-2 uppercase tracking-wider">{language === 'fr' ? 'Opérations' : 'Operations'}</h4>
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b border-gray-700">
                          <th className="text-left p-2 text-gray-400">{language === 'fr' ? 'Mois' : 'Month'}</th>
                          <th className="text-right p-2 text-gray-400">{language === 'fr' ? 'Écoles' : 'Schools'}</th>
                          <th className="text-right p-2 text-gray-400">{language === 'fr' ? 'Garderies' : 'Daycares'}</th>
                          <th className="text-right p-2 text-gray-400">{language === 'fr' ? 'Établ.' : 'Inst.'}</th>
                          <th className="text-right p-2 text-gray-400">{language === 'fr' ? 'Traiteurs' : 'Caterers'}</th>
                          <th className="text-right p-2 text-gray-400">{language === 'fr' ? 'Repas/jour' : 'Meals/day'}</th>
                          <th className="text-right p-2 text-gray-400">{language === 'fr' ? 'GMV ann.' : 'Ann. GMV'}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.map((m, i) => (
                          <tr key={i} className={`border-b border-gray-800 ${m.milestone ? 'bg-lime-500/10' : ''}`}>
                            <td className="p-2 text-white font-medium">{language === 'fr' ? m.label : m.labelEn}{m.milestone && <span className="ml-2 px-1.5 py-0.5 bg-lime-500/30 text-lime-300 text-[9px] rounded uppercase">{m.milestone}</span>}</td>
                            <td className="p-2 text-right text-gray-300">{m.ecoles.toLocaleString()}</td>
                            <td className="p-2 text-right text-gray-300">{m.garderies.toLocaleString()}</td>
                            <td className="p-2 text-right text-cyan-400 font-semibold">{m.etablissements.toLocaleString()}</td>
                            <td className="p-2 text-right text-gray-300">{m.traiteurs}</td>
                            <td className="p-2 text-right text-gray-300">{m.repasJour.toLocaleString()}</td>
                            <td className="p-2 text-right text-orange-400 font-semibold">{(m.gmvAnnualise / 1000000).toFixed(1)}M $</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Table Financier */}
                  <div className="overflow-x-auto">
                    <h4 className="text-sm font-semibold text-cyan-400 mb-2 uppercase tracking-wider">{language === 'fr' ? 'Financier' : 'Financial'}</h4>
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b border-gray-700">
                          <th className="text-left p-2 text-gray-400">{language === 'fr' ? 'Mois' : 'Month'}</th>
                          <th className="text-right p-2 text-gray-400">{language === 'fr' ? 'Revenu mensuel' : 'Monthly revenue'}</th>
                          <th className="text-right p-2 text-gray-400">{language === 'fr' ? 'Dépenses' : 'Expenses'}</th>
                          <th className="text-right p-2 text-gray-400">{language === 'fr' ? 'Résultat' : 'Result'}</th>
                          <th className="text-right p-2 text-gray-400">{language === 'fr' ? 'Cash estimé' : 'Estimated cash'}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.map((m, i) => (
                          <tr key={i} className={`border-b border-gray-800 ${m.milestone ? 'bg-cyan-500/10' : ''}`}>
                            <td className="p-2 text-white font-medium">{language === 'fr' ? m.label : m.labelEn}</td>
                            <td className="p-2 text-right text-lime-400 font-semibold">{(m.revenuMensuel / 1000).toFixed(1)}K $</td>
                            <td className="p-2 text-right text-gray-300">{(m.depenses / 1000).toFixed(1)}K $</td>
                            <td className={`p-2 text-right font-semibold ${m.resultat >= 0 ? 'text-lime-400' : 'text-orange-400'}`}>{m.resultat >= 0 ? '+' : ''}{(m.resultat / 1000).toFixed(1)}K $</td>
                            <td className="p-2 text-right text-cyan-400 font-semibold">{m.cash >= 1000000 ? `${(m.cash / 1000000).toFixed(2)}M $` : `${(m.cash / 1000).toFixed(1)}K $`}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="p-3 bg-black/30 rounded-lg border border-gray-700">
                    <p className="text-[11px] text-gray-400">
                      {language === 'fr'
                        ? `Dépenses ${preSeedInvestment.useOfFunds.gtm.toLocaleString()}$/6 mois (GTM) + ${(preSeedInvestment.useOfFunds.development + preSeedInvestment.useOfFunds.ledger + preSeedInvestment.useOfFunds.legal + preSeedInvestment.useOfFunds.marketing + preSeedInvestment.useOfFunds.reserve).toLocaleString()}$ (autres) = ${preSeedInvestment.total.toLocaleString()}$ pré-seed total. Dépenses passent de ~83K$/mois à ~66K$/mois après M6 (post-GTM intense).`
                        : `Expenses $${preSeedInvestment.useOfFunds.gtm.toLocaleString()}/6 months (GTM) + $${(preSeedInvestment.useOfFunds.development + preSeedInvestment.useOfFunds.ledger + preSeedInvestment.useOfFunds.legal + preSeedInvestment.useOfFunds.marketing + preSeedInvestment.useOfFunds.reserve).toLocaleString()} (other) = $${preSeedInvestment.total.toLocaleString()} total pre-seed. Expenses drop from ~$83K/month to ~$66K/month after M6 (post intensive GTM).`}
                    </p>
                  </div>
                </div>
              );
            })()}
          </CardContent>
        </Card>

        {/* === LEGACY 5-YEAR PROJECTIONS removed — focus Y1 only === */}


        {/* Vidéo Renard en bas */}
        <VideoPlayer 
          videoUrl={process.env.REACT_APP_FOX_VIDEO_URL}
          posterUrl={process.env.REACT_APP_FOX_POSTER_URL}
          title={t.videoTitle}
        />

        {/* Navigation controls */}
        <NavigationControls />
        </div>
      </div>
    </div>
  );
}
